import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RefreshCw, Download, Printer, Filter, Calendar, FileText, User, Hash, CheckCircle, ChevronDown, CreditCard, DollarSign } from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';

const PaymentReport = () => {
  const { t } = useLanguage();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [payments, setPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]); // For Excel export
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const observerRef = useRef();
  const [filter, setFilter] = useState({
    dateFrom: new Date().toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0],
    keyword: '',
  });

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = useCallback(async (reset = true) => {
    if (reset) {
      setLoading(true);
      setSkip(0);
      setPayments([]);
      setAllPayments([]);
      setHasMore(true);
    } else {
      setIsLoadingMore(true);
    }

    const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
    if (!tokenData) {
      showError('Please login first');
      return;
    }

    const userData = JSON.parse(tokenData);

    try {
      const filterData = {
        DateFrom: new Date(filter.dateFrom),
        DateTo: new Date(filter.dateTo),
        Keyword: filter.keyword,
        ClientIds: [],
        FromMonth: new Date(filter.dateFrom).getMonth() + 1,
        FromDay: new Date(filter.dateFrom).getDate(),
        FromYear: new Date(filter.dateFrom).getFullYear(),
        ToMonth: new Date(filter.dateTo).getMonth() + 1,
        ToDay: new Date(filter.dateTo).getDate(),
        ToYear: new Date(filter.dateTo).getFullYear(),
        ClientId: userData.ClientId,
        Skip: reset ? 0 : skip,
        Take: 20
      };

      const response = await MainService.ClientPaymentClientInList(filterData, userData.Token);
      
      let paymentsData = [];
      let totalCount = 0;
      let totalAmount = 0;

      // Handle response structure similar to CustomerReport
      if (response && response.Data && Array.isArray(response.Data)) {
        paymentsData = response.Data;
        totalCount = response.Total || response.Data.length;
        // Calculate total amount from the data
        totalAmount = paymentsData.reduce((sum, payment) => sum + (payment.ClientMoney || 0), 0);
      } else if (Array.isArray(response)) {
        paymentsData = response;
        totalCount = response.length;
        totalAmount = paymentsData.reduce((sum, payment) => sum + (payment.ClientMoney || 0), 0);
      } else if (response && Array.isArray(response.data)) {
        paymentsData = response.data;
        totalCount = response.data.length;
        totalAmount = paymentsData.reduce((sum, payment) => sum + (payment.ClientMoney || 0), 0);
      } else {
        console.warn('Unexpected payments response structure:', response);
        paymentsData = [];
        totalCount = 0;
        totalAmount = 0;
      }

      if (reset) {
        setPayments(paymentsData);
        setAllPayments(paymentsData);
        setTotalCount(totalCount);
        setTotalAmount(totalAmount);
        showSuccess(`Loaded ${paymentsData.length} payment records successfully`);
        console.log('Reset load - Payments:', paymentsData.length, 'Total:', totalCount);
      } else {
        setPayments(prev => {
          const newPayments = [...prev, ...paymentsData];
          console.log('Appending payments - Previous:', prev.length, 'New:', paymentsData.length, 'Total:', newPayments.length);
          return newPayments;
        });
        setAllPayments(prev => {
          const newAllPayments = [...prev, ...paymentsData];
          console.log('Appending to allPayments - Previous:', prev.length, 'New:', paymentsData.length, 'Total:', newAllPayments.length);
          return newAllPayments;
        });
        // Update total amount
        setTotalAmount(prev => prev + paymentsData.reduce((sum, payment) => sum + (payment.ClientMoney || 0), 0));
      }

      setSkip(prev => prev + 20);
      // Check if we have more payments to load
      const currentLoadedCount = reset ? paymentsData.length : (allPayments.length + paymentsData.length);
      const hasMoreData = currentLoadedCount < totalCount;
      setHasMore(hasMoreData);

      console.log('Pagination Debug:', {
        paymentsDataLength: paymentsData.length,
        currentLoadedCount,
        totalCount,
        hasMoreData,
        skip: skip + 20,
        remainingPayments: totalCount - currentLoadedCount
      });
    } catch (error) {
      console.error('Error loading payments:', error);
      showError('Failed to load payment reports');
      if (reset) {
        setPayments([]);
        setAllPayments([]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [filter, skip, showError, showSuccess, allPayments.length]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilter = () => {
    loadPayments(true);
  };

  // Infinite scroll observer
  const lastPaymentElementRef = useCallback(node => {
    if (isLoadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      console.log('Observer triggered:', {
        isIntersecting: entries[0].isIntersecting,
        hasMore,
        isLoadingMore,
        currentPaymentsLength: payments.length
      });
      if (entries[0].isIntersecting && hasMore) {
        console.log('Loading more payments...');
        loadPayments(false);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [isLoadingMore, hasMore, loadPayments, payments.length]);

  const handleRefresh = () => {
    setFilter({
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      keyword: '',
    });
    loadPayments(true);
  };

  const handlePrint = async (paymentData = null) => {
    try {
      setIsPrinting(true);

      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login first');
        return;
      }

      const userData = JSON.parse(tokenData);

      if (paymentData) {
        // Print individual payment
        const response = await MainService.PrintIndClientPayment(paymentData.Id, userData.Token);
        if (response) {
          window.open(`${process.env.REACT_APP_BASE_PATH || ''}/ReportFiles/${response}`, '_blank');
          showSuccess('Print request sent successfully');
        } else {
          showError('Print Error');
        }
      } else {
        // Print summary
        const filterData = {
          DateFrom: new Date(filter.dateFrom),
          DateTo: new Date(filter.dateTo),
          Keyword: filter.keyword,
          ClientIds: [],
          FromMonth: new Date(filter.dateFrom).getMonth() + 1,
          FromDay: new Date(filter.dateFrom).getDate(),
          FromYear: new Date(filter.dateFrom).getFullYear(),
          ToMonth: new Date(filter.dateTo).getMonth() + 1,
          ToDay: new Date(filter.dateTo).getDate(),
          ToYear: new Date(filter.dateTo).getFullYear(),
          ClientId: userData.ClientId
        };

        const response = await MainService.PrintIndClientPaymentSummary(filterData, userData.Token);
        if (response) {
          window.open(`${process.env.REACT_APP_BASE_PATH || ''}/ReportFiles/${response}`, '_blank');
          showSuccess('Print summary request sent successfully');
        } else {
          showError('Print Error');
        }
      }
    } catch (error) {
      console.error('Print error:', error);
      showError('Print Error');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleExportToExcel = () => {
    if (!Array.isArray(allPayments) || allPayments.length === 0) {
      showError('No data to export');
      return;
    }

    try {
      // Prepare data for Excel export
      const excelData = allPayments.map((payment, index) => ({
        'S.No': index + 1,
        'Date': formatDate(payment.ClientPayment?.Date),
        'Ref No': payment.RefNo || 'N/A',
        'Main Ref No': payment.ClientPayment?.RefNo || 'N/A',
        'Total Orders': payment.TotalOrders || 'N/A',
        'Status': payment.Status || 'N/A',
        'Amount': payment.ClientMoney ? `AED ${payment.ClientMoney}` : 'N/A',
        'Client Name': payment.Client?.Name || 'N/A'
      }));

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payment Reports');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Payment_Reports_${currentDate}.xlsx`;

      // Save the file
      XLSX.writeFile(wb, filename);
      showSuccess('Excel file exported successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      showError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `AED ${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('paymentReport.title')}</h1>
          <p className="text-gray-600">{t('paymentReport.subtitle')}</p>
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('paymentReport.filters')}</h2>
              <p className="text-sm text-gray-600 mt-1">Filter your payment records by date and keywords</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleFilter}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Filter className="w-5 h-5" />
                <span>{t('common.applyFilters')}</span>
              </button>
              <button
                onClick={handleRefresh}
                className="bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-xl transition-all duration-200 flex items-center space-x-2 shadow-md hover:shadow-lg"
              >
                <RefreshCw className="w-5 h-5" />
                <span>{t('common.reset')}</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                From Date
              </label>
              <input
                type="date"
                value={filter.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-primary-600" />
                To Date
              </label>
              <input
                type="date"
                value={filter.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-800 mb-2 flex items-center">
                <Search className="w-4 h-4 mr-2 text-primary-600" />
                Keyword
              </label>
              <input
                type="text"
                placeholder="Search payments..."
                value={filter.keyword}
                onChange={(e) => handleFilterChange('keyword', e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-gray-50 hover:bg-white placeholder-gray-400"
              />
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Records</p>
                <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Loaded Records</p>
                <p className="text-2xl font-bold text-gray-900">{allPayments.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(totalAmount)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('paymentReport.records')}</h2>
              <p className="text-sm text-gray-600 mt-1">Detailed view of all payment records</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleExportToExcel}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <Download className="w-4 h-4" />
                <span>{t('common.exportExcel')}</span>
              </button>
              <button
                onClick={() => handlePrint()}
                disabled={isPrinting}
                className={`bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${isPrinting ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
              >
                {isPrinting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Printing...</span>
                  </>
                ) : (
                  <>
                    <Printer className="w-4 h-4" />
                    <span>Print Summary</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto overflow-y-visible rounded-xl border border-gray-200">
            <table className="w-full" style={{ minWidth: '800px' }}>
              <thead>
                <tr className="bg-gradient-to-r from-gray-100 to-gray-200">
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">#</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Date</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Ref No</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Main Ref No</th>
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Total Orders</th>
                  <th className="px-4 py-4 text-left text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Status</th>
                  <th className="px-4 py-4 text-right text-xs font-bold text-gray-800 uppercase tracking-wider border-r border-gray-300">Amount</th>
                  <th className="px-4 py-4 text-center text-xs font-bold text-gray-800 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                          <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">Loading payments...</p>
                        <p className="text-sm text-gray-500 mt-1">Please wait while we fetch your data</p>
                      </div>
                    </td>
                  </tr>
                ) : !Array.isArray(payments) || payments.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <CreditCard className="w-8 h-8 text-gray-400" />
                        </div>
                        <p className="text-lg font-medium text-gray-700">No payments found</p>
                        <p className="text-sm text-gray-500 mt-1">Try adjusting your filters to see more results</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  payments.map((payment, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 transition-all duration-200 group border-b border-gray-100"
                      ref={index === payments.length - 1 ? lastPaymentElementRef : null}
                    >
                      <td className="px-4 py-4 text-center text-sm text-gray-500 font-medium border-r border-gray-100">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {formatDate(payment.ClientPayment?.Date)}
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 group-hover:text-primary-700 border-r border-gray-100">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                          <span>{payment.RefNo || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 border-r border-gray-100">
                        {payment.ClientPayment?.RefNo || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-center border-r border-gray-100">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">
                          {payment.TotalOrders || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm border-r border-gray-100">
                        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                          {payment.Status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-bold text-green-600 text-right border-r border-gray-100">
                        {formatCurrency(payment.ClientMoney)}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => handlePrint(payment)}
                          className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
                          title="Print Payment"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Loading More Indicator */}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-2"></div>
              <span className="text-gray-600">Loading more payments...</span>
            </div>
          )}

          {/* Load More Button (Fallback) */}
          {hasMore && payments.length > 0 && !isLoadingMore && (
            <div className="text-center py-4">
              <button
                onClick={() => loadPayments(false)}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
              >
                Load More Payments ({totalCount - allPayments.length} remaining)
              </button>
            </div>
          )}

          {/* End of Results */}
          {!hasMore && payments.length > 0 && !isLoadingMore && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No more payments to load
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PaymentReport;
